import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Users, UserPlus, HandCoins, UsersRound, TrendingUp, Baby, GraduationCap, Video,
  Pin, BarChart3, FolderUp, CalendarPlus, Megaphone, Heart, Droplets,
  Calendar as CalIcon, CalendarOff, FileText, Link2, ArrowRight, Clock,
} from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import DevotionalCard from "@/components/DevotionalCard";
import SermonNotesCard from "@/components/SermonNotesCard";
import InstallPromptBanner from "@/components/InstallPromptBanner";
import KPICard, { KPICardSkeleton } from "@/components/KPICard";
// RecentAdminActivityCard was previously rendered on this page for admins.
// It has been moved off the homepage per operator request and now lives
// exclusively on the /audit-log route. The import is retained (commented)
// only so future contributors know where the card component lives.
// import { RecentAdminActivityCard } from "@/pages/AuditLog";

const KPI_META = {
  attendance:             { label: "Worship Center", icon: Users,          color: "#B2674A", isCurrency: false },
  fc_kids_attendance:     { label: "FC Kids",        icon: Baby,           color: "#DCA591", isCurrency: false },
  fc_students_attendance: { label: "FC Students",    icon: GraduationCap,  color: "#AEBDB1", isCurrency: false },
  online_audience:        { label: "Online",         icon: Video,          color: "#425252", isCurrency: false },
  giving:                 { label: "Giving",         icon: HandCoins,      color: "#C69E5F", isCurrency: true  },
  guests:                 { label: "Guests",         icon: UserPlus,       color: "#DCA591", isCurrency: false },
  volunteers:             { label: "Serving",        icon: UsersRound,     color: "#425252", isCurrency: false },
  small_groups:           { label: "Small Groups",   icon: UsersRound,     color: "#AEBDB1", isCurrency: false },
  salvations:             { label: "Salvations",     icon: Heart,          color: "#B2674A", isCurrency: false },
  baptisms:               { label: "Baptisms",       icon: Droplets,       color: "#7FA1B5", isCurrency: false },
};

const fmtVal = (v, currency) => {
  if (v == null) return "—";
  if (currency) return `$${Math.round(Number(v)).toLocaleString()}`;
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });
};
const eventStart = (e) => e?.start?.dateTime || (e?.start?.date ? `${e.start.date}T00:00:00` : null);
const isAllDay = (e) => !!e?.start?.date && !e?.start?.dateTime;

export default function Home() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [calStatus, setCalStatus] = useState(null);
  const [todayEvents, setTodayEvents] = useState([]);
  const [weekEvents, setWeekEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [recentKpis, setRecentKpis] = useState([]);
  const [recentDocs, setRecentDocs] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", body: "", pinned: false, expires_at: "" });
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // {id, title} | null
  const [deletingAnn, setDeletingAnn] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const annUrl = isAdmin && showExpired ? "/announcements?include_expired=1" : "/announcements";
    const [s, cs, a, k, d] = await Promise.all([
      api.get("/kpi-summary"),
      api.get("/calendar/google/status"),
      api.get(annUrl),
      api.get("/kpi-entries"),
      api.get("/documents"),
    ]);
    setSummary(s.data);
    setCalStatus(cs.data);
    setAnnouncements(a.data);
    setRecentKpis(k.data);
    setRecentDocs(d.data);
    if (cs.data?.connected) {
      const [t, w] = await Promise.all([
        api.get("/calendar/google/events?range=today"),
        api.get("/calendar/google/events?range=week"),
      ]);
      setTodayEvents(t.data.events || []);
      setWeekEvents(w.data.events || []);
    } else {
      setTodayEvents([]);
      setWeekEvents([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showExpired]);

  const submitAnnouncement = async () => {
    if (!annForm.title.trim() || !annForm.body.trim()) return toast.error("Title and body are required");
    try {
      const payload = {
        title: annForm.title,
        body: annForm.body,
        expires_at: (annForm.expires_at || "").trim() || null,
        // pinned is silently ignored server-side for non-admins on POST,
        // and for non-admin authors on PATCH. Mirror that here so the
        // edit dialog never silently discards an admin-toggled flag.
        ...(isAdmin ? { pinned: !!annForm.pinned } : {}),
      };
      if (editingId) {
        await api.patch(`/announcements/${editingId}`, payload);
        toast.success("Announcement updated");
      } else {
        await api.post("/announcements", { ...payload, pinned: isAdmin ? !!annForm.pinned : false });
        toast.success("Announcement posted");
      }
      closeAnnouncementDialog();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save announcement");
    }
  };

  const openEditAnnouncement = (a) => {
    setEditingId(a.id);
    setAnnForm({
      title: a.title || "",
      body: a.body || "",
      pinned: !!a.pinned,
      expires_at: a.expires_at || "",
    });
    setAnnOpen(true);
  };

  const closeAnnouncementDialog = () => {
    setAnnOpen(false);
    setEditingId(null);
    setAnnForm({ title: "", body: "", pinned: false, expires_at: "" });
  };

  const togglePin = async (a) => {
    if (!isAdmin) return;
    try {
      await api.patch(`/announcements/${a.id}`, { pinned: !a.pinned });
      load();
    } catch {
      toast.error("Couldn't update pin");
    }
  };

  const confirmDeleteAnnouncement = async () => {
    if (!pendingDelete?.id) return;
    const id = pendingDelete.id;
    setDeletingAnn(true);
    // Optimistic remove so the UI feels instant.
    const snapshot = announcements;
    setAnnouncements((prev) => prev.filter((x) => x.id !== id));
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Announcement deleted");
      setPendingDelete(null);
      load(); // pull fresh server state (handles ordering / expiry)
    } catch (e) {
      // Roll back the optimistic removal on failure.
      setAnnouncements(snapshot);
      const status = e?.response?.status;
      toast.error(
        status === 403
          ? "Only an admin can delete announcements."
          : status === 404
          ? "This announcement no longer exists."
          : e?.response?.data?.detail || "Couldn't delete announcement."
      );
    } finally {
      setDeletingAnn(false);
    }
  };

  const canEditAnnouncement = (a) => isAdmin || a.author_id === user?.user_id;

  // Next upcoming events beyond "today" that fall inside this week — used for sidebar preview
  const laterThisWeek = weekEvents
    .filter((e) => {
      const s = eventStart(e);
      if (!s) return false;
      const d = parseISO(s);
      const endOfToday = new Date(); endOfToday.setHours(23, 59, 59, 999);
      return d > endOfToday;
    })
    .slice(0, 3);

  // Build a per-category sparkline series (last 10 weeks, oldest → newest)
  // from the kpi_entries we already fetched. Falls back to empty array,
  // which the KPICard renders as a flat baseline. Manual entries always win
  // over auto-imported ones for the same week to mirror the dashboard logic.
  const sparkFor = (cat) => {
    const rows = (recentKpis || []).filter((k) => k.category === cat);
    const byWeek = new Map();
    for (const r of rows) {
      const prev = byWeek.get(r.week_of);
      if (!prev || (r.source === "manual" && prev.source !== "manual")) {
        byWeek.set(r.week_of, r);
      }
    }
    const sorted = Array.from(byWeek.values()).sort((a, b) => a.week_of.localeCompare(b.week_of));
    return sorted.slice(-10).map((r) => ({ value: Number(r.value || 0) }));
  };

  // 5 ministry-priority KPIs answering "How is the church doing right now?"
  // Serving (`volunteers`) was removed from this rail per operator request —
  // the underlying KPI_META entry and database category are preserved so
  // any historical volunteer entries still render on the KPI detail page.
  const priorityKpis = ["giving", "attendance", "guests", "small_groups", "salvations"];

  // Real-data freshness — surfaced once at the section header, not on every card.
  // /api/kpi-summary returns `fetched_at` as ISO UTC.
  const summaryFetchedAt = summary?.fetched_at;
  const lastUpdatedLabel = summaryFetchedAt
    ? formatDistanceToNow(parseISO(summaryFetchedAt), { addSuffix: true })
    : null;

  return (
    <div className="space-y-8 md:space-y-10" data-testid="home-dashboard">
      {/* ---------- 1. Greeting (compact, at the very top) ---------- */}
      <div data-testid="home-greeting">
        <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Command center</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-primary mt-1">
          Hello, {user?.name?.split(" ")[0] ?? "friend"}.
        </h2>
      </div>

      {/* PWA install banner — auto-hides when already installed or dismissed */}
      <InstallPromptBanner />

      {/* ---------- 2. Daily Devotional (prominent) ---------- */}
      <DevotionalCard />

      {/* ---------- 3. This Week's Sermon Notes ---------- */}
      <SermonNotesCard />

      {/* Recent admin activity was moved OFF the homepage per operator
          request — admins now find it exclusively on the dedicated
          /audit-log page (linked from the Admin menu). We intentionally
          do NOT duplicate it here. */}

      {/* ---------- 4. Calendar event sections ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Today's events */}
        <Card className="lg:col-span-2 rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="today-events-card">
          <div className="px-6 py-5 border-b border-brand-primary/10 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Today</p>
              <h3 className="font-heading text-xl text-brand-primary mt-0.5">
                {format(new Date(), "EEEE, MMMM d")}
              </h3>
            </div>
            <Badge className="rounded-full bg-terracotta/10 text-terracotta border-0" data-testid="today-events-count">
              {todayEvents.length} {todayEvents.length === 1 ? "event" : "events"}
            </Badge>
          </div>
          <ul className="divide-y divide-brand-primary/10" data-testid="today-events-list">
            {!calStatus?.connected ? (
              <EmptyRow icon={CalendarOff} title="Google Calendar not connected"
                body={user?.role === "admin"
                  ? <>Link it in <Link to="/settings" className="text-terracotta hover:underline">Settings → Integrations</Link>.</>
                  : "Ask an admin to connect it in Settings."}
              />
            ) : todayEvents.length === 0 ? (
              <EmptyRow icon={CalIcon} title="Nothing scheduled today"
                body="No events on the calendar for today. Breathe." />
            ) : (
              todayEvents.slice(0, 5).map((e) => (
                <EventRow key={e.id} ev={e} />
              ))
            )}
          </ul>
        </Card>

        {/* This week summary */}
        <Card className="rounded-2xl bg-brand-primary text-white shadow-warm-lg p-6 flex flex-col" data-testid="this-week-card">
          <p className="text-[11px] uppercase tracking-[0.18em] text-peach font-semibold">This week</p>
          <p className="font-heading text-5xl font-light mt-2" data-testid="this-week-event-count">
            {weekEvents.length}
          </p>
          <p className="text-sm text-white/75 mt-0.5">
            {weekEvents.length === 1 ? "event on the calendar" : "events on the calendar"}
          </p>
          <div className="mt-5 pt-5 border-t border-white/15 space-y-3 flex-1">
            {laterThisWeek.length === 0 ? (
              <p className="text-sm text-white/70">
                {calStatus?.connected ? "Nothing else scheduled this week." : "Connect Google Calendar to see what's ahead."}
              </p>
            ) : (
              laterThisWeek.map((e) => {
                const start = eventStart(e);
                const d = start ? parseISO(start) : null;
                const allDay = isAllDay(e);
                return (
                  <div key={e.id} className="flex items-start gap-3">
                    <div className="text-center shrink-0 w-11">
                      <p className="text-[10px] uppercase tracking-wider text-peach font-semibold">{d ? format(d, "EEE") : "—"}</p>
                      <p className="font-heading text-xl">{d ? format(d, "d") : "—"}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{e.summary || "(no title)"}</p>
                      <p className="text-xs text-white/65 truncate">
                        {d ? (allDay ? "All day" : format(d, "h:mm a")) : ""}
                        {e.location ? ` · ${e.location}` : ""}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <Link
            to="/calendar"
            className="inline-flex items-center justify-between mt-5 pt-4 border-t border-white/15 text-sm text-peach hover:text-white transition"
            data-testid="home-open-calendar"
          >
            Open full calendar <ArrowRight className="h-4 w-4" />
          </Link>
        </Card>
      </div>

      {/* ---------- 3. Pulse — KPI metrics strip ---------- */}
      <section data-testid="home-pulse-section">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Pulse</p>
            <h3 className="font-heading text-xl sm:text-2xl font-medium text-brand-primary mt-0.5">
              How is the church doing right now?
            </h3>
            {lastUpdatedLabel && (
              <p className="text-xs text-brand-primary/45 mt-1 inline-flex items-center gap-1.5" data-testid="home-kpi-last-updated">
                <Clock className="h-3 w-3" /> Updated {lastUpdatedLabel}
              </p>
            )}
          </div>
          <Link
            to="/kpi"
            className="text-sm text-terracotta hover:text-terracotta-hover hover:underline inline-flex items-center gap-1 transition-colors"
            data-testid="home-kpi-view-all"
          >
            All KPIs <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="home-kpi-strip">
          {summary == null
            ? Array.from({ length: priorityKpis.length }).map((_, i) => <KPICardSkeleton key={i} />)
            : priorityKpis.map((cat) => {
                const meta = KPI_META[cat];
                const card = summary?.cards?.[cat] || {};
                const notConnected =
                  card?.source === "pushpay_placeholder" ||
                  card?.source === "ccb_not_connected" ||
                  card?.source === "ccb_unavailable" ||
                  card?.source === "ccb_error";
                // Pick the most decision-useful delta per category:
                //  - giving → MoM% from Pushpay
                //  - others → prev-week / prev-period delta
                const givingMom = cat === "giving" && card?.mom_pct != null ? card.mom_pct : null;
                const delta = givingMom != null
                  ? givingMom
                  : ((!notConnected && card?.primary_value != null && card?.prev_period_value)
                      ? ((card.primary_value - card.prev_period_value) / card.prev_period_value) * 100
                      : null);
                const deltaLabel = givingMom != null
                  ? "vs last month"
                  : (delta != null ? "vs prev period" : (card?.rule_label || ""));
                const notConnectedLabel =
                  card?.source === "pushpay_placeholder" ? "Pushpay · not connected"
                  : card?.source === "ccb_not_connected" ? "ChMS · not connected"
                  : card?.source === "ccb_unavailable" ? "ChMS · not available"
                  : card?.source === "ccb_error" ? "ChMS · error"
                  : "Not connected";
                const href = cat === "giving" ? "/giving" : "/kpi";
                return (
                  <KPICard
                    key={cat}
                    label={meta.label}
                    icon={meta.icon}
                    color={meta.color}
                    value={fmtVal(card?.primary_value, meta.isCurrency)}
                    delta={delta}
                    deltaLabel={deltaLabel}
                    notConnected={notConnected}
                    notConnectedLabel={notConnectedLabel}
                    sparkline={sparkFor(cat)}
                    href={href}
                    testId={`home-kpi-${cat}`}
                  />
                );
              })}
        </div>
      </section>

      {/* ---------- 4. Quick actions ---------- */}
      <section data-testid="quick-actions">
        <h3 className="font-heading text-xl font-medium text-brand-primary mb-4">Quick actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link
            to="/kpi"
            data-testid="qa-add-kpi"
            className="group rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg flex items-start gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-terracotta/10 grid place-items-center shrink-0">
              <BarChart3 className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-brand-primary">Add KPI entry</p>
              <p className="text-xs text-brand-primary/55 mt-0.5">Log this week's numbers</p>
            </div>
          </Link>

          <button
            onClick={() => setAnnOpen(true)}
            data-testid="qa-add-announcement"
            className="group text-left rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg flex items-start gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-gold/15 grid place-items-center shrink-0">
              <Megaphone className="h-5 w-5 text-gold" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-brand-primary">Add announcement</p>
              <p className="text-xs text-brand-primary/55 mt-0.5">Share with the team</p>
            </div>
          </button>

          <Link
            to="/calendar"
            data-testid="qa-add-event"
            className="group rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg flex items-start gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-sage/25 grid place-items-center shrink-0">
              <CalendarPlus className="h-5 w-5 text-brand-primary" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-brand-primary">Add event</p>
              <p className="text-xs text-brand-primary/55 mt-0.5">Post to Google Calendar</p>
            </div>
          </Link>

          <Link
            to="/documents"
            data-testid="qa-upload-doc"
            className="group rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg flex items-start gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-peach/40 grid place-items-center shrink-0">
              <FolderUp className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-brand-primary">Upload document</p>
              <p className="text-xs text-brand-primary/55 mt-0.5">HR, policy, forms</p>
            </div>
          </Link>
        </div>
      </section>

      {/* ---------- 5. Recent activity ---------- */}
      <section>
        <h3 className="font-heading text-xl font-medium text-brand-primary mb-4">Recent activity</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="recent-activity">
          {/* Announcements */}
          <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Announcements</p>
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <label className="flex items-center gap-1.5 text-[11px] text-brand-primary/65 cursor-pointer" data-testid="ann-show-expired-toggle">
                    <Switch
                      checked={showExpired}
                      onCheckedChange={setShowExpired}
                      className="scale-75 data-[state=checked]:bg-terracotta"
                    />
                    Show expired
                  </label>
                )}
                <button
                  onClick={() => setAnnOpen(true)}
                  data-testid="recent-new-announcement"
                  className="text-xs text-terracotta hover:underline"
                >
                  + New
                </button>
              </div>
            </div>
            {announcements.length === 0 ? (
              <p className="text-sm text-brand-primary/55">Nothing posted yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="recent-announcements">
                {announcements.slice(0, 4).map((a) => {
                  const expDate = a.expires_at ? parseISO(a.expires_at) : null;
                  const todayIso = new Date().toISOString().slice(0, 10);
                  const isExpired = !!a.expires_at && a.expires_at < todayIso;
                  return (
                    <li key={a.id} className={`min-w-0 ${isExpired ? "opacity-50" : ""}`} data-testid={`ann-row-${a.id}`}>
                      <div className="flex items-center gap-1.5 group">
                        {a.pinned && <Pin className="h-3 w-3 text-terracotta shrink-0" data-testid="ann-pinned-badge" />}
                        <p className="font-medium text-sm text-brand-primary truncate flex-1">{a.title}</p>
                        {canEditAnnouncement(a) && (
                          <button
                            onClick={() => openEditAnnouncement(a)}
                            className="text-[10px] uppercase tracking-wider text-brand-primary/40 hover:text-terracotta transition shrink-0 px-1.5 py-1 md:px-0 md:py-0 md:opacity-0 md:group-hover:opacity-100"
                            data-testid={`ann-edit-${a.id}`}
                          >
                            Edit
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => togglePin(a)}
                            className="text-[10px] uppercase tracking-wider text-brand-primary/40 hover:text-terracotta transition shrink-0 px-1.5 py-1 md:px-0 md:py-0 md:opacity-0 md:group-hover:opacity-100"
                            data-testid={`ann-pin-toggle-${a.id}`}
                          >
                            {a.pinned ? "Unpin" : "Pin"}
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => setPendingDelete({ id: a.id, title: a.title })}
                            className="text-[10px] uppercase tracking-wider text-brand-primary/40 hover:text-terracotta transition shrink-0 px-1.5 py-1 md:px-0 md:py-0 md:opacity-0 md:group-hover:opacity-100"
                            data-testid={`ann-delete-${a.id}`}
                            aria-label="Delete announcement"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-brand-primary/65 mt-0.5 line-clamp-2">{a.body}</p>
                      <p className="text-[10px] uppercase tracking-wider text-brand-primary/45 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{a.author_name} · {formatDistanceToNow(parseISO(a.created_at), { addSuffix: true })}</span>
                        {expDate && (
                          <span className="text-[10px] normal-case tracking-normal text-brand-primary/55">
                            {isExpired ? "Expired" : "Expires"} {format(expDate, "MMM d")}
                          </span>
                        )}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* KPI updates */}
          <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">KPI updates</p>
              <Link to="/kpi" className="text-xs text-terracotta hover:underline">View all</Link>
            </div>
            {recentKpis.length === 0 ? (
              <p className="text-sm text-brand-primary/55">No entries yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="recent-kpis">
                {recentKpis.slice(0, 3).map((k) => {
                  const meta = KPI_META[k.category];
                  return (
                    <li key={k.id} className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${meta?.color || "#425252"}1A` }}>
                        {meta?.icon ? <meta.icon className="h-4 w-4" style={{ color: meta.color }} strokeWidth={1.7} /> : <TrendingUp className="h-4 w-4 text-terracotta" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-brand-primary truncate">
                          {meta?.label || k.category}: {fmtVal(k.value, meta?.isCurrency)}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-brand-primary/45 mt-0.5">
                          Week of {format(parseISO(k.week_of), "MMM d")} · added {formatDistanceToNow(parseISO(k.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* Documents */}
          <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Documents</p>
              <Link to="/documents" className="text-xs text-terracotta hover:underline">Hub</Link>
            </div>
            {recentDocs.length === 0 ? (
              <p className="text-sm text-brand-primary/55">No documents uploaded yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="recent-docs">
                {recentDocs.slice(0, 3).map((d) => (
                  <li key={d.id} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-terracotta/10 grid place-items-center shrink-0">
                      {d.file_name ? <FileText className="h-4 w-4 text-terracotta" /> : <Link2 className="h-4 w-4 text-terracotta" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-brand-primary truncate">{d.title}</p>
                      <p className="text-[10px] uppercase tracking-wider text-brand-primary/45 mt-0.5">
                        {(d.category || "").replace("_", " ")} · {formatDistanceToNow(parseISO(d.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </section>

      {/* Announcement dialog (create + edit) */}
      <Dialog open={annOpen} onOpenChange={(o) => (o ? setAnnOpen(true) : closeAnnouncementDialog())}>
        <DialogContent className="sm:max-w-lg" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? "Edit announcement" : "Post announcement"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              data-testid="announcement-title-input"
              placeholder="Title"
              value={annForm.title}
              onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })}
            />
            <Textarea
              data-testid="announcement-body-input"
              placeholder="What do you want to share with staff?"
              rows={5}
              value={annForm.body}
              onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isAdmin && (
                <label className="flex items-center gap-3 text-sm text-brand-primary border border-brand-primary/15 rounded-xl px-3 py-2.5">
                  <Switch
                    data-testid="announcement-pinned-switch"
                    checked={annForm.pinned}
                    onCheckedChange={(v) => setAnnForm({ ...annForm, pinned: v })}
                  />
                  Pin to top
                </label>
              )}
              <label className="text-sm text-brand-primary/80 flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wider text-brand-primary/55">
                  Auto-hide after (optional)
                </span>
                <Input
                  type="date"
                  data-testid="announcement-expires-input"
                  value={annForm.expires_at || ""}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setAnnForm({ ...annForm, expires_at: e.target.value })}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="announcement-submit-button"
              onClick={submitAnnouncement}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
            >
              {editingId ? "Save changes" : "Post announcement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin-only delete confirmation. Authors are intentionally blocked
          server-side too — see require_admin on DELETE /api/announcements/{id}. */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o && !deletingAnn) setPendingDelete(null); }}
      >
        <AlertDialogContent data-testid="ann-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? (
                <>
                  Are you sure you want to delete <strong>"{pendingDelete.title}"</strong>?
                  This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ann-delete-cancel" disabled={deletingAnn}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="ann-delete-confirm-button"
              onClick={(e) => { e.preventDefault(); confirmDeleteAnnouncement(); }}
              disabled={deletingAnn}
              className="bg-terracotta hover:bg-terracotta-hover text-white"
            >
              {deletingAnn ? "Deleting..." : "Delete announcement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- helpers ----------
function EmptyRow({ icon: Icon, title, body }) {
  return (
    <li className="p-6 flex items-start gap-4 text-brand-primary/70 text-sm">
      <Icon className="h-5 w-5 text-terracotta/70 mt-0.5 shrink-0" />
      <div>
        <p className="font-medium text-brand-primary">{title}</p>
        <p className="mt-1">{body}</p>
      </div>
    </li>
  );
}

function EventRow({ ev }) {
  const s = eventStart(ev);
  const d = s ? parseISO(s) : null;
  const allDay = isAllDay(ev);
  return (
    <li className="p-5 flex items-start gap-4 hover:bg-cream/60 transition">
      <div className="w-14 text-center shrink-0 rounded-xl py-1.5 bg-cream/80">
        <p className="text-[10px] uppercase tracking-wider text-terracotta font-semibold">
          {d ? format(d, "MMM") : "--"}
        </p>
        <p className="font-heading text-xl text-brand-primary -mt-0.5">
          {d ? format(d, "d") : "--"}
        </p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-brand-primary truncate">{ev.summary || "(no title)"}</p>
        <p className="text-sm text-brand-primary/65 mt-0.5 inline-flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          {d ? (allDay ? "All day" : format(d, "h:mm a")) : "No time"}
          {ev.location ? <span className="text-brand-primary/45">· {ev.location}</span> : null}
        </p>
      </div>
    </li>
  );
}
